/**
 * ellmo-ai drops — scroll-driven product media + review crossfade.
 * Scroll progress through the product info column advances a small set of
 * gallery frames. Variant changes jump to that variant's featured media.
 */
(function () {
  var STAGE_SEL = '[data-ellmo-ai-scroll-stage]';
  var SLIDE_SEL = '[data-ellmo-ai-slide]';
  var REVIEW_SEL = '[data-ellmo-ai-review-slide]';
  var DOT_SEL = '[data-ellmo-ai-dot]';
  var TRANSITION_MS = 350;
  /* Cap how many gallery frames scroll can advance through — fewer, calmer swaps. */
  var MAX_SCROLL_IMAGES = 4;

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function setActiveGroup(nodes, index) {
    if (!nodes || !nodes.length) return;
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      var active = i === index;
      nodes[i].classList.toggle('is-active', active);
      if (active) {
        nodes[i].removeAttribute('aria-hidden');
      } else {
        nodes[i].setAttribute('aria-hidden', 'true');
      }
    }
  }

  function initialsFromName(name) {
    if (!name) return '';
    var parts = String(name)
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return '';
    var letters = parts[0].charAt(0);
    if (parts.length > 1) {
      letters += parts[parts.length - 1].charAt(0);
    }
    return letters.toUpperCase();
  }

  function fillReviewAvatars(root) {
    var reviews = root.querySelectorAll(REVIEW_SEL);
    Array.prototype.forEach.call(reviews, function (review) {
      var avatar = review.querySelector('[data-ellmo-ai-avatar]');
      var nameEl = review.querySelector('[data-ellmo-ai-review-name]');
      if (!avatar || !nameEl) return;
      avatar.textContent = initialsFromName(nameEl.textContent);
    });
  }

  function parseVariantMediaMap(stage) {
    var raw = stage.getAttribute('data-variant-media');
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch (err) {
      return {};
    }
  }

  function visibleSlides(stage) {
    return Array.prototype.slice.call(stage.querySelectorAll(SLIDE_SEL)).filter(function (slide) {
      return !slide.hasAttribute('hidden');
    });
  }

  function teardownStage(stage) {
    if (typeof stage._ellmoAiTeardown === 'function') {
      stage._ellmoAiTeardown();
      stage._ellmoAiTeardown = null;
    }
    stage.removeAttribute('data-ellmo-ai-scroll-ready');
  }

  function initStage(stage) {
    if (stage.getAttribute('data-ellmo-ai-scroll-ready') === 'true') return;
    stage.setAttribute('data-ellmo-ai-scroll-ready', 'true');

    var section =
      stage.closest('.section-featured-product-drops') ||
      stage.closest('section') ||
      document;
    var productInfo = stage.closest('product-info') || section;
    var mediaRoot = stage.querySelector('.ellmo-ai-scroll-stage__media') || stage;
    var allSlides = Array.prototype.slice.call(stage.querySelectorAll(SLIDE_SEL));
    var reviews = Array.prototype.slice.call(stage.querySelectorAll(REVIEW_SEL));
    var dots = Array.prototype.slice.call(stage.querySelectorAll(DOT_SEL));
    var variantMediaMap = parseVariantMediaMap(stage);
    var hideVariants = stage.getAttribute('data-hide-variants') === 'true';
    var unsubscribeVariant = null;
    var scrollRaf = 0;
    var lockTimer = null;
    /* After a variant swap, hold featured (index 0) until scroll clearly leaves that band. */
    var pinUntilProgress = null;

    fillReviewAvatars(stage);

    if (!allSlides.length) {
      stage.removeAttribute('data-ellmo-ai-scroll-ready');
      return;
    }

    var activeIndex = 0;
    var locked = false;
    var slides = visibleSlides(stage);

    function refreshSlideList() {
      allSlides = Array.prototype.slice.call(stage.querySelectorAll(SLIDE_SEL));
      slides = visibleSlides(stage);
    }

    function reindexVisibleSlides() {
      slides = visibleSlides(stage);
      slides.forEach(function (slide, index) {
        slide.setAttribute('data-index', String(index));
      });
      stage.setAttribute('data-image-count', String(Math.min(slides.length, MAX_SCROLL_IMAGES)));
    }

    function promoteSlide(slide) {
      if (!slide || !mediaRoot || slide.parentNode !== mediaRoot) return;
      if (mediaRoot.firstElementChild !== slide) {
        mediaRoot.insertBefore(slide, mediaRoot.firstElementChild);
      }
      refreshSlideList();
      reindexVisibleSlides();
    }

    function getScrollProgress() {
      var progressRoot =
        productInfo.querySelector('.product__info-container') ||
        section.querySelector('.product__info-container') ||
        productInfo;
      if (!progressRoot) return 0;
      var rootRect = progressRoot.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var absoluteTop = rootRect.top + window.scrollY;
      var start = absoluteTop - viewport * 0.1;
      var end = absoluteTop + rootRect.height * 0.75 - viewport * 0.35;
      var range = Math.max(1, end - start);
      return (window.scrollY - start) / range;
    }

    function clearLockTimer() {
      if (lockTimer) {
        window.clearTimeout(lockTimer);
        lockTimer = null;
      }
    }

    function armLock(ms) {
      clearLockTimer();
      locked = true;
      lockTimer = window.setTimeout(function () {
        locked = false;
        lockTimer = null;
      }, ms || TRANSITION_MS);
    }

    function applyIndex(rawIndex, force) {
      if (locked && !force) return;
      slides = visibleSlides(stage);
      if (!slides.length) return;

      var next = Math.max(0, Math.min(rawIndex, slides.length - 1));
      activeIndex = next;

      var imageIndex = next;
      var reviewIndex = reviews.length ? next % reviews.length : 0;
      var dotIndex = dots.length ? Math.min(imageIndex, dots.length - 1) : 0;

      setActiveGroup(slides, imageIndex);
      setActiveGroup(reviews, reviewIndex);
      setActiveGroup(dots, dotIndex);

      var activeSlide = slides[imageIndex];
      if (activeSlide && activeSlide.getAttribute('data-media-id')) {
        stage.setAttribute('data-active-media-id', activeSlide.getAttribute('data-media-id'));
      }
    }

    function activateMediaId(mediaId) {
      if (mediaId == null || mediaId === '') return false;
      var targetId = String(mediaId);
      refreshSlideList();
      var match = allSlides.find(function (slide) {
        return slide.getAttribute('data-media-id') === targetId;
      });
      if (!match) return false;

      if (hideVariants) {
        allSlides.forEach(function (slide) {
          var isVariantImage = slide.getAttribute('data-variant-image') === 'true';
          var isMatch = slide === match;
          if (isVariantImage && !isMatch) {
            slide.setAttribute('hidden', '');
            slide.classList.remove('is-active');
            slide.setAttribute('aria-hidden', 'true');
          } else {
            slide.removeAttribute('hidden');
          }
        });
      }

      /* Keep the active variant featured image first so scroll track stays correct. */
      match.removeAttribute('hidden');
      promoteSlide(match);

      var trackLen = Math.max(1, Math.min(visibleSlides(stage).length, MAX_SCROLL_IMAGES));
      var progress = getScrollProgress();
      var currentBand = progressToIndex(progress, trackLen);
      /* Hold featured until the shopper scrolls into the next progress band. */
      pinUntilProgress = (currentBand + 1) / trackLen;

      armLock(TRANSITION_MS);
      applyIndex(0, true);
      return true;
    }

    function mediaIdForVariant(variant) {
      if (!variant) return null;
      if (variant.featured_media && variant.featured_media.id != null) {
        return variant.featured_media.id;
      }
      if (variant.id != null && variantMediaMap[String(variant.id)] != null) {
        return variantMediaMap[String(variant.id)];
      }
      return null;
    }

    function syncFromVariant(variant) {
      var mediaId = mediaIdForVariant(variant);
      if (mediaId == null) return;
      activateMediaId(mediaId);
    }

    var initialMediaId = stage.getAttribute('data-active-media-id');
    if (initialMediaId) {
      activateMediaId(initialMediaId);
    } else {
      applyIndex(0);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data) return;
        if (event.data.sectionId && productInfo && productInfo.dataset) {
          var sectionId = productInfo.dataset.section || productInfo.sectionId;
          if (sectionId && event.data.sectionId !== sectionId) return;
        }
        syncFromVariant(event.data.variant);
      });
    }

    function scrollTrack() {
      return visibleSlides(stage).slice(0, MAX_SCROLL_IMAGES);
    }

    function progressToIndex(progress, count) {
      if (count <= 1) return 0;
      var clamped = Math.max(0, Math.min(0.999, progress));
      return Math.floor(clamped * count);
    }

    function setActiveSlide(track, index) {
      var visible = visibleSlides(stage);
      var target = track[index];
      visible.forEach(function (slide) {
        var on = slide === target;
        slide.classList.toggle('is-active', on);
        if (on) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
      if (target && target.getAttribute('data-media-id')) {
        stage.setAttribute('data-active-media-id', target.getAttribute('data-media-id'));
      }
    }

    function updateFromScroll() {
      if (locked) return;
      var track = scrollTrack();
      if (!track.length) return;

      var progress = getScrollProgress();

      if (pinUntilProgress != null && progress < pinUntilProgress) {
        if (activeIndex !== 0) {
          activeIndex = 0;
          setActiveSlide(track, 0);
        }
        setActiveGroup(reviews, 0);
        setActiveGroup(dots, 0);
        return;
      }
      if (pinUntilProgress != null && progress >= pinUntilProgress) {
        pinUntilProgress = null;
      }

      var imageIndex = progressToIndex(progress, track.length);
      var reviewIndex = reviews.length ? progressToIndex(progress, Math.min(reviews.length, MAX_SCROLL_IMAGES)) : 0;
      var dotIndex = dots.length ? Math.min(imageIndex, dots.length - 1) : 0;

      if (imageIndex !== activeIndex) {
        activeIndex = imageIndex;
        setActiveSlide(track, imageIndex);
      }
      setActiveGroup(reviews, reviewIndex);
      setActiveGroup(dots, dotIndex);
    }

    function onScroll() {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(function () {
        scrollRaf = 0;
        updateFromScroll();
      });
    }

    if (!prefersReducedMotion()) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      updateFromScroll();
    }

    stage._ellmoAiTeardown = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearLockTimer();
      pinUntilProgress = null;
      if (scrollRaf) {
        window.cancelAnimationFrame(scrollRaf);
        scrollRaf = 0;
      }
      if (typeof unsubscribeVariant === 'function') {
        unsubscribeVariant();
        unsubscribeVariant = null;
      }
    };
  }

  function boot() {
    var stages = document.querySelectorAll(STAGE_SEL);
    if (!stages.length) return;
    Array.prototype.forEach.call(stages, initStage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll(STAGE_SEL).forEach(teardownStage);
    boot();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event && event.target ? event.target : document;
    root.querySelectorAll(STAGE_SEL).forEach(teardownStage);
  });
})();
